import React, { useState, useEffect, useRef } from "react";
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { blurActiveEditableElement, scrollFocusedElementIntoView } from '@/utils/keyboardInset';
import ResponsiveOverlay from '@/components/ResponsiveOverlay/ResponsiveOverlay';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import OnboardingSplashBackground from '@/components/Login/onboarding/OnboardingSplashBackground';
import InputField from "../Login/InputField";
import travelerBackground from "../../assets/images/traveller_background.png";
import logoOrangeIcon from "../../assets/logo-orange.svg";
import google from "../../assets/images/google.png";
import apple from "../../assets/images/apple.png";
import { loginUser, signUpUser, getOAuthSignInUrl, startOAuthSignIn, forgotPassword, confirmPassword, resendVerificationCode, PasswordResetError } from '@/lib/cognito';
import { useIsInAppBrowser } from '@/lib/browser-detection';
import { decodeIdToken } from '@/utils/tokenUtils';
import { useAuth } from '@/contexts';
import { toast } from 'react-toastify';
import { debug, error as logError } from '@/utils/logger';
import { getUtmsFromCurrentUrl, encodeOAuthState, getUtmsWithFallback } from '@/lib/utm';
import { setPostAuthRedirect } from '@/lib/postAuthRedirect';
import { deferOnboardingForSession } from '@/lib/onboardingDeferral';
import { getPasswordValidationError, COGNITO_INVALID_PASSWORD_MESSAGE } from '@/utils/passwordValidation';
import { isNativePlatform } from '@/lib/platform';
import { nativeAlert } from '@/lib/nativeDialog';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_AUTH_MODAL_DISMISSED,
  EVENT_AUTH_MODAL_OPENED,
  EVENT_AUTH_METHOD_SELECTED,
  EVENT_AUTH_METHOD_STEP_VIEWED,
  EVENT_AUTH_MODE_TOGGLED,
  EVENT_AUTH_TRANSITION_SHOWN,
  EVENT_FORGOT_PASSWORD_CLICKED,
  EVENT_LOGIN,
  EVENT_PASSWORD_RESET_COMPLETED,
  EVENT_RESET_CODE_SENT,
} from '@/lib/analyticsEvents';
import {
  ACCOUNT_SYNC_ALERT_MESSAGE,
  ACCOUNT_SYNC_ALERT_TITLE,
  INVALID_CREDENTIALS_ALERT_MESSAGE,
  INVALID_CREDENTIALS_ALERT_TITLE,
  isInvalidCredentialsError,
  isUsernameExistsError,
  USERNAME_EXISTS_ALERT_MESSAGE,
  USERNAME_EXISTS_ALERT_TITLE,
} from '@/lib/authErrorFeedback';
import "./LoginModal.css";
import BannedUserDialog from '../BannedUserDialog/BannedUserDialog';
import { isBannedUserError } from '@/api/auth';
import { holdCollisionAndClearSession, isAccountLinkRequiredError, isEmailUnverifiedError } from '@/lib/accountLinking';
import axios from 'axios';

type ModalMode = 'login' | 'signup' | 'forgotPassword' | 'resetPassword';

interface Props {
  onClose: () => void;
  redirectPath?: string;
  initialMode?: 'login' | 'signup';
  /** Figma auth drawer: compact white sheet over splash (no hero image on method step). */
  variant?: 'default' | 'figma-auth';
  /**
   * Email sign-up / log-in stay on the page and call this instead of navigating
   * (Google / Apple still leave for the provider and return to `redirectPath`).
   */
  onAuthenticated?: () => void;
  /** Let an account that hasn't finished onboarding skip it for the rest of this session. */
  deferOnboarding?: boolean;
  /** Replaces the default sign-up / log-in blurb, e.g. to say why an account is needed here. */
  description?: string;
  /**
   * Details the visitor already gave (e.g. the Plan Your Trip form): skips the method
   * picker and only asks for a password. The email is fixed and `name` goes on the new
   * account. If the email already has an account, the modal switches to log in.
   */
  prefilledAccount?: { email: string; name?: string };
}

const SIGNUP_METHOD_DESCRIPTION =
  'Discover travel inspiration from creators, friends, or social media, and save/remix it into your own trip.';

const LoginModal: React.FC<Props> = ({ 
  onClose, 
  redirectPath,
  initialMode = 'signup',
  variant = 'default',
  onAuthenticated,
  deferOnboarding = false,
  description,
  prefilledAccount,
}) => {
  const isInAppBrowser = useIsInAppBrowser();
  const isMobileViewport = useIsMobileViewport();
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [isAuthTransitioning, setIsAuthTransitioning] = useState<boolean>(false);
  const [mode, setMode] = useState<ModalMode>(initialMode === 'login' ? 'login' : 'signup');
  const [showEmailAuth, setShowEmailAuth] = useState<boolean>(Boolean(prefilledAccount));
  const [loading, setLoading] = useState<boolean>(false);
  const [form, setForm] = useState<{
    email: string;
    password: string;
  }>({
    email: prefilledAccount?.email ?? '',
    password: ''
  });
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    verificationCode?: string;
    newPassword?: string;
    confirmPassword?: string;
    general?: string;
  }>({});
  
  // Password reset specific state
  const [resetEmail, setResetEmail] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPasswordValue, setConfirmPasswordValue] = useState<string>('');
  const [codeSent, setCodeSent] = useState<boolean>(false);
  const [canResendCode, setCanResendCode] = useState<boolean>(false);
  const [resendTimer, setResendTimer] = useState<number>(0);
  const [showResendVerification, setShowResendVerification] = useState<boolean>(false);
  
  // Banned user state
  const [showBannedDialog, setShowBannedDialog] = useState<boolean>(false);
  const [bannedMessage, setBannedMessage] = useState<string>('');
  const [isDeletedAccount, setIsDeletedAccount] = useState<boolean>(false);
  
  const router = useNavigate();
  const contentRef = useRef<HTMLDivElement>(null);
  const { login, completeOnboarding } = useAuth();

  // Capture UTM parameters on mount
  React.useEffect(() => {
    getUtmsFromCurrentUrl();
    WebAnalytics.track(EVENT_AUTH_MODAL_OPENED, {
      mode: initialMode,
      variant,
      entry: redirectPath ? 'redirect' : 'default',
    });
  }, [initialMode, redirectPath, variant]);

  // Resend code timer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resendTimer === 0 && codeSent) {
      setCanResendCode(true);
    }
  }, [resendTimer, codeSent]);

  useEffect(() => {
    const onMethodStep = (mode === 'login' || mode === 'signup') && !showEmailAuth;
    if (onMethodStep) {
      WebAnalytics.track(EVENT_AUTH_METHOD_STEP_VIEWED, { mode, variant });
    }
  }, [mode, showEmailAuth, variant]);

  useEffect(() => {
    if (isAuthTransitioning) {
      WebAnalytics.track(EVENT_AUTH_TRANSITION_SHOWN, { mode, variant });
    }
  }, [isAuthTransitioning, mode, variant]);

  const handleClose = () => {
    WebAnalytics.track(EVENT_AUTH_MODAL_DISMISSED, { mode, variant });
    blurActiveEditableElement();
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 150);
  };

  const finishAuthSuccess = (destination: '/onboarding' | string) => {
    blurActiveEditableElement();
    if (destination === '/onboarding') {
      router('/onboarding', { replace: true });
    } else if (destination) {
      router(destination);
    }
    onClose();
  };

  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateVerificationCode = (code: string): boolean => {
    // 6 digits
    const re = /^\d{6}$/;
    return re.test(code);
  };

  const maskEmail = (email: string): string => {
    if (!email) return '';
    const [localPart, domain] = email.split('@');
    if (!localPart || !domain) return email;
    const maskedLocal = localPart.length > 2 
      ? `${localPart[0]}${'*'.repeat(Math.min(localPart.length - 2, 3))}${localPart[localPart.length - 1]}`
      : `${localPart[0]}*`;
    return `${maskedLocal}@${domain}`;
  };

  const setAuthData = (result: { accessToken: string; refreshToken: string; idToken: string }): void => {
    localStorage.setItem('accessToken', result.accessToken);
    localStorage.setItem('refreshToken', result.refreshToken);
    localStorage.setItem('idToken', result.idToken);
    localStorage.setItem('isLogin', 'true');
  };

  const getUserDetail = async () => {
    try {
      // Prefer the idToken we just wrote to localStorage in setAuthData(); fall back
      // to Amplify's session for callers that didn't pre-populate it (e.g. an
      // existing tab that already had a session). On native, Amplify's storage
      // doesn't always surface tokens written by amazon-cognito-identity-js, so
      // localStorage is the authoritative source for our custom flow.
      let idToken = typeof window !== 'undefined' ? localStorage.getItem('idToken') : null;
      if (!idToken) {
        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession();
        idToken = session?.tokens?.idToken?.toString() ?? null;
      }

      if (!idToken) {
        logError('No ID token available for user sync');
        toast.error('Authentication error. Please try logging in again.');
        return null;
      }

      const { email, cognitoUsername } = decodeIdToken(idToken);

      if (email && cognitoUsername) {
        const utmData = getUtmsWithFallback();
        const { syncUserWithBackend } = await import('@/api/auth');
        const { runWithProfileSyncRetry } = await import('@/lib/syncUserProfileWithRetry');
        const user = await runWithProfileSyncRetry(
          () => syncUserWithBackend(email, cognitoUsername, utmData),
          'LoginModal.getUserDetail',
        );

        if (user && typeof user === 'object' && Object.keys(user).length > 0) {
          debug('🔐 [LoginModal.getUserDetail] User synced, onboarding status:', {
            didCompleteOnboarding: user.did_complete_onboarding,
            userId: user._id,
            email: user.email
          });
          
          if (user.did_complete_onboarding) {
            completeOnboarding();
          }

          login(user);
          return user;
        } else {
          logError('Invalid user data received from backend');
          toast.error('Authentication error. Please try logging in again.');
          return null;
        }
      } else {
        logError('Cannot extract email/username from token');
        toast.error('Authentication error. Please try logging in again.');
        return null;
      }
    } catch (error: unknown) {
      // Check for banned or deleted user error - re-throw so it can be handled in login catch block
      // Also check axios error responses for banned/deleted account errors
      const isAxiosBanned = axios.isAxiosError(error) && error.response?.status === 403 && error.response?.data?.error === 'Account banned';
      const isAxiosDeleted = axios.isAxiosError(error) && error.response?.status === 403 && error.response?.data?.error === 'Account deleted';
      const errorObj = error as Record<string, unknown>;
      if (isBannedUserError(error) || errorObj?.isBanned || errorObj?.isDeleted || isAxiosBanned || isAxiosDeleted) {
        // Re-throw banned/deleted errors so they can be handled in the login catch block
        throw error;
      }
      
      logError('Error syncing user with backend:', error);
      // Re-throw so the login flow can handle it (rather than treating a network error as "not onboarded")
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    const newErrors: { email?: string; password?: string } = {};

    if (!form.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(form.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!form.password) {
      newErrors.password = 'Password is required';
    } else {
      const passwordError = getPasswordValidationError(form.password);
      if (passwordError) {
        newErrors.password = passwordError;
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    if (isMobileViewport || isNativePlatform()) {
      setIsAuthTransitioning(true);
    }

    try {
      if (mode === 'login') {
        // Login flow
        const result = await loginUser(form.email, form.password);
        
        // Save tokens to localStorage before getUserDetail so syncUserWithBackend can read auth headers
        setAuthData(result);
        // Before getUserDetail signs the user in, so the layout's onboarding redirect sees it.
        if (deferOnboarding) deferOnboardingForSession();

        // Try to get user details - this will throw if account is banned/deleted
        try {
          const syncedUser = await getUserDetail();
          
          debug('🔐 [LoginModal] After login, checking onboarding status', {
            syncedUserDidCompleteOnboarding: syncedUser?.did_complete_onboarding,
            userId: syncedUser?._id
          });
          
          if (syncedUser && onAuthenticated) {
            WebAnalytics.track(EVENT_LOGIN, {
              method: 'email',
              mode: 'login',
              variant,
              ...(syncedUser.did_complete_onboarding ? {} : { needs_onboarding: true }),
            });
            onAuthenticated();
            finishAuthSuccess('');
          } else if (syncedUser?.did_complete_onboarding) {
            debug('🔐 [LoginModal] User has completed onboarding - closing modal and redirecting');
            WebAnalytics.track(EVENT_LOGIN, {
              method: 'email',
              mode: 'login',
              variant,
            });
            if (redirectPath) {
              finishAuthSuccess(redirectPath);
            } else {
              finishAuthSuccess('/');
            }
          } else {
            debug('🔐 [LoginModal] User has NOT completed onboarding - redirecting to /onboarding for onboarding');
            WebAnalytics.track(EVENT_LOGIN, {
              method: 'email',
              mode: 'login',
              variant,
              needs_onboarding: true,
            });
            setPostAuthRedirect(redirectPath);
            finishAuthSuccess('/onboarding');
          }
        } catch (userDetailError: unknown) {
          // Check for banned or deleted account errors from getUserDetail
          const isAxiosBanned = axios.isAxiosError(userDetailError) && userDetailError.response?.status === 403 && userDetailError.response?.data?.error === 'Account banned';
          const isAxiosDeleted = axios.isAxiosError(userDetailError) && userDetailError.response?.status === 403 && userDetailError.response?.data?.error === 'Account deleted';
          const userDetailErrorObj = userDetailError as Record<string, unknown>;
          if (userDetailErrorObj?.isBanned || isBannedUserError(userDetailError) || isAxiosBanned) {
            const message = (userDetailErrorObj?.bannedMessage as string | undefined) || (axios.isAxiosError(userDetailError) ? userDetailError.response?.data?.message : undefined) || 'Your account has been suspended. Please contact support for assistance.';
            // Clear any partial auth state
            localStorage.removeItem('accessToken');
            localStorage.removeItem('idToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('isLogin');
            setBannedMessage(message);
            setIsDeletedAccount(false);
            setShowBannedDialog(true);
            setIsAuthTransitioning(false);
            return;
          } else if (userDetailErrorObj?.isDeleted || isAxiosDeleted) {
            const message = (userDetailErrorObj?.deletedMessage as string | undefined) || (axios.isAxiosError(userDetailError) ? userDetailError.response?.data?.message : undefined) || 'This account has been deleted. Please contact support for assistance.';
            // Clear any partial auth state
            localStorage.removeItem('accessToken');
            localStorage.removeItem('idToken');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('isLogin');
            setBannedMessage(message);
            setIsDeletedAccount(true);
            setShowBannedDialog(true);
            setIsAuthTransitioning(false);
            return;
          }
          // If it's not a banned/deleted error, re-throw to outer catch
          throw userDetailError;
        }
      } else {
        // Signup flow
        const result = await signUpUser({ ...form, name: prefilledAccount?.name });
        setAuthData(result);
        if (deferOnboarding) deferOnboardingForSession();

        // Sync user with backend
        let backendUser = null;
        try {
          const { email, cognitoUsername } = decodeIdToken(result.idToken);

          if (!email || !cognitoUsername) {
            throw new Error('Cannot extract email/username from signup response');
          }

          const utmData = getUtmsWithFallback();
          const { syncUserWithBackend, createUser } = await import('@/api/auth');
          backendUser = await syncUserWithBackend(email, cognitoUsername, utmData);

          // The name typed with the request goes on the profile; onboarding can still change it.
          if (prefilledAccount?.name && !backendUser.name) {
            try {
              backendUser = await createUser({ name: prefilledAccount.name });
            } catch (nameError) {
              logError('[LoginModal] Failed to save the prefilled name:', nameError);
            }
          }

          if (backendUser.did_complete_onboarding) {
            completeOnboarding();
          }
        } catch (syncError) {
          if (isAccountLinkRequiredError(syncError) || isEmailUnverifiedError(syncError)) {
            throw syncError;
          }
          logError('[LoginModal] Failed to sync user with backend:', syncError);
        }

        // Set user state before redirecting to prevent race condition
        // This ensures LayoutWrapper sees authenticated user when checking onboarding status
        if (backendUser) {
          login(backendUser);
          WebAnalytics.trackSignup({
            id: backendUser._id,
            email: backendUser.email,
            name: backendUser.name,
            profile_type: backendUser.profile_type || 'regular',
          }, {
            method: 'email',
            variant,
          });
        } else {
          login();
        }

        // Even if the profile sync failed, the caller's own request surfaces that error in
        // context — better than dropping a guest into onboarding mid-task.
        if (onAuthenticated) {
          onAuthenticated();
          finishAuthSuccess('');
          return;
        }

        debug('🔐 [LoginModal] Signup successful - redirecting to /onboarding for onboarding');
        setPostAuthRedirect(redirectPath);
        finishAuthSuccess('/onboarding');
      }
    } catch (err: unknown) {
      setIsAuthTransitioning(false);
      if (isAccountLinkRequiredError(err) && holdCollisionAndClearSession(err.existingMethods, redirectPath ?? null, deferOnboarding)) {
        router('/auth/callback');
        return;
      }
      if (isEmailUnverifiedError(err)) {
        toast.error('Your email address is not verified. Please verify your email first.');
        return;
      }
      logError(mode === 'login' ? 'Login error:' : 'Signup error:', err);
      const errObj = err as Record<string, unknown>;
      if (mode === 'login') {
        // Check for banned or deleted account errors (from Cognito or other sources)
        const isAxiosBanned = axios.isAxiosError(err) && err.response?.status === 403 && err.response?.data?.error === 'Account banned';
        const isAxiosDeleted = axios.isAxiosError(err) && err.response?.status === 403 && err.response?.data?.error === 'Account deleted';
        
        if (errObj?.isBanned || isBannedUserError(err) || isAxiosBanned) {
          const message = (errObj?.bannedMessage as string | undefined) || (axios.isAxiosError(err) ? err.response?.data?.message : undefined) || (err instanceof Error ? err.message : undefined) || 'Your account has been suspended. Please contact support for assistance.';
          // Clear any partial auth state
          localStorage.removeItem('accessToken');
          localStorage.removeItem('idToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('isLogin');
          setBannedMessage(message);
          setIsDeletedAccount(false);
          setShowBannedDialog(true);
        } else if (errObj?.isDeleted || isAxiosDeleted) {
          const message = (errObj?.deletedMessage as string | undefined) || (axios.isAxiosError(err) ? err.response?.data?.message : undefined) || (err instanceof Error ? err.message : undefined) || 'This account has been deleted. Please contact support for assistance.';
          // Clear any partial auth state
          localStorage.removeItem('accessToken');
          localStorage.removeItem('idToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('isLogin');
          setBannedMessage(message);
          setIsDeletedAccount(true);
          setShowBannedDialog(true);
        } else if (axios.isAxiosError(err)) {
          if (isNativePlatform()) {
            await nativeAlert({
              title: ACCOUNT_SYNC_ALERT_TITLE,
              message: ACCOUNT_SYNC_ALERT_MESSAGE,
            });
          } else {
            toast.error('Failed to load your account data. Please try again.');
          }
        } else {
          const error = err as { message?: string };
          if (isNativePlatform() && isInvalidCredentialsError(err)) {
            await nativeAlert({
              title: INVALID_CREDENTIALS_ALERT_TITLE,
              message: INVALID_CREDENTIALS_ALERT_MESSAGE,
            });
          } else {
            toast.error(error.message || 'Login failed. Please check your credentials.');
          }
        }
      } else {
        const error = err as { message?: string };
        if (prefilledAccount && isUsernameExistsError(err)) {
          // The email they gave already has an account: ask for that password instead.
          setMode('login');
          setForm((prev) => ({ ...prev, password: '' }));
          setErrors({});
          return;
        }
        if (isNativePlatform() && isUsernameExistsError(err)) {
          await nativeAlert({
            title: USERNAME_EXISTS_ALERT_TITLE,
            message: USERNAME_EXISTS_ALERT_MESSAGE,
          });
        } else {
          toast.error(error.message || 'Signup failed. Please try again.');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    const newErrors: { email?: string; general?: string } = {};

    if (!resetEmail) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(resetEmail)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);
    setErrors({});

    try {
      await forgotPassword(resetEmail);
      WebAnalytics.track(EVENT_RESET_CODE_SENT, { method: 'email' });
      setCodeSent(true);
      setCanResendCode(false);
      setResendTimer(120);
    } catch (err: unknown) {
      const error = err as PasswordResetError;
      logError('Forgot password error:', err);
      
      if (error.code === 'UNVERIFIED_EMAIL') {
        setErrors({
          general: 'Your email address is not verified. Please verify your email first.',
        });
        setShowResendVerification(true);
      } else if (error.code === 'InvalidParameterException' && (error.message?.includes('Google') || error.message?.includes('Apple'))) {
        setErrors({
          general: 'This account uses Google/Apple sign-in. Please sign in with your provider instead.',
        });
      } else {
        // Generic success message for security (don't reveal if email exists)
        setCodeSent(true);
        setCanResendCode(false);
        setResendTimer(120);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerificationEmail = async (): Promise<void> => {
    setLoading(true);
    try {
      await resendVerificationCode(resetEmail);
      setShowResendVerification(false);
    } catch (err: unknown) {
      logError('Resend verification error:', err);
      toast.error('Unable to send verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async (): Promise<void> => {
    if (!canResendCode) return;
    
    setLoading(true);
    try {
      await forgotPassword(resetEmail);
      setCanResendCode(false);
      setResendTimer(120);
    } catch (err: unknown) {
      logError('Resend code error:', err);
      toast.error('Unable to send code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    const newErrors: {
      verificationCode?: string;
      newPassword?: string;
      confirmPassword?: string;
    } = {};

    if (!verificationCode) {
      newErrors.verificationCode = 'Verification code is required';
    } else if (!validateVerificationCode(verificationCode)) {
      newErrors.verificationCode = 'Please enter the 6-digit verification code';
    }

    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else {
      const passwordError = getPasswordValidationError(newPassword);
      if (passwordError) {
        newErrors.newPassword = passwordError;
      }
    }

    if (!confirmPasswordValue) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (newPassword !== confirmPasswordValue) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);

    try {
      await confirmPassword(resetEmail, verificationCode, newPassword);
      WebAnalytics.track(EVENT_PASSWORD_RESET_COMPLETED, { method: 'email' });
      // Reset state and return to email login form
      setMode('login');
      setShowEmailAuth(true);
      setForm({ email: resetEmail, password: '' });
      setResetEmail('');
      setVerificationCode('');
      setNewPassword('');
      setConfirmPasswordValue('');
      setCodeSent(false);
      setErrors({});
    } catch (err: unknown) {
      const error = err as PasswordResetError;
      logError('Reset password error:', err);
      
      if (error.code === 'CodeMismatchException') {
        setErrors({
          verificationCode: 'Invalid verification code. Please try again.'
        });
      } else if (error.code === 'ExpiredCodeException') {
        setErrors({
          verificationCode: 'This verification code has expired. Please request a new code.',
          general: 'Code expired'
        });
        setCanResendCode(true);
      } else if (error.code === 'InvalidPasswordException') {
        setErrors({
          newPassword: COGNITO_INVALID_PASSWORD_MESSAGE
        });
      } else {
        toast.error('Unable to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthClick = async (provider: 'Google' | 'Apple'): Promise<void> => {
    WebAnalytics.track(EVENT_AUTH_METHOD_SELECTED, {
      method: provider.toLowerCase(),
      mode,
      variant,
    });
    try {
      const utms = getUtmsFromCurrentUrl();
      const encodedState = encodeOAuthState({ utms, redirectPath: redirectPath || null, deferOnboarding });
      const oauthUrl = getOAuthSignInUrl(provider, encodedState);

      // Close modal and redirect to OAuth flow
      handleClose();
      await startOAuthSignIn(oauthUrl);
    } catch (error: unknown) {
      logError(`Error signing in with ${provider}:`, error);
      toast.error(`Failed to sign in with ${provider}. Please try again.`);
    }
  };

  const getTitle = (): string => {
    if (prefilledAccount && mode === 'signup') return 'Create a password';
    if (prefilledAccount && mode === 'login') return 'Enter your password';
    switch (mode) {
      case 'forgotPassword':
        return codeSent ? 'Check Your Email' : 'Reset Password';
      case 'resetPassword':
        return 'Reset Password';
      case 'login':
        return showEmailAuth ? 'Log in with email' : 'Welcome back';
      default:
        return showEmailAuth ? 'Sign up with email' : 'Join Globe Thrivers';
    }
  };

  const isMethodStep = (mode === 'login' || mode === 'signup') && !showEmailAuth;
  const isFigmaAuthMethod = variant === 'figma-auth' && isMethodStep;
  const isMobileSheetLayout = isMobileViewport || isNativePlatform();
  const useFigmaMobileSheet = variant === 'figma-auth' && isMobileSheetLayout;
  const useFigmaMethodStep = variant === 'figma-auth' || isMethodStep;
  const isFormStep =
    ((mode === 'login' || mode === 'signup') && showEmailAuth) ||
    mode === 'forgotPassword' ||
    mode === 'resetPassword';
  const mobileSheetPanelClass = useFigmaMobileSheet
    ? `login-modal-figma-mobile-sheet${isFormStep ? ' login-modal-figma-mobile-sheet--form' : ''}`
    : 'login-modal-mobile-sheet';

  useEffect(() => {
    const contentEl = contentRef.current;
    if (!contentEl || !isMobileSheetLayout) return;

    const native = isNativePlatform();
    const scrollFocusedField = () => {
      scrollFocusedElementIntoView(contentEl, { padding: 24 });
    };

    const handleFocusIn = () => {
      if (native) {
        window.setTimeout(scrollFocusedField, 120);
        return;
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(scrollFocusedField);
      });
    };

    contentEl.addEventListener('focusin', handleFocusIn);
    const viewport = native ? null : window.visualViewport;
    viewport?.addEventListener('resize', scrollFocusedField);
    viewport?.addEventListener('scroll', scrollFocusedField);

    return () => {
      contentEl.removeEventListener('focusin', handleFocusIn);
      viewport?.removeEventListener('resize', scrollFocusedField);
      viewport?.removeEventListener('scroll', scrollFocusedField);
    };
  }, [isMobileSheetLayout, mode, showEmailAuth]);

  const getDescription = (): string => {
    if (prefilledAccount && mode === 'login') {
      return `You already have an account with ${form.email}. Log in to continue.`;
    }
    if (description && (mode === 'signup' || mode === 'login')) return description;
    switch (mode) {
      case 'forgotPassword':
        return codeSent 
          ? `We've sent a 6-digit verification code to ${maskEmail(resetEmail)}. Please check your inbox.`
          : 'Enter your email address and we will send you a verification code to reset your password.';
      case 'resetPassword':
        return 'Enter the verification code you received and your new password.';
      case 'signup':
        return useFigmaMethodStep ? SIGNUP_METHOD_DESCRIPTION : 'Get instant access to travel inspo, AI-powered itineraries, and trips shared by explorers like you!';
      default:
        return 'Get instant access to travel inspo, AI-powered itineraries, and trips shared by explorers like you!';
    }
  };

  const showHero =
    !(useFigmaMethodStep && isMethodStep) && !(isMobileSheetLayout && isFormStep);

  const modalContent = (
    <ResponsiveOverlay
      onClose={handleClose}
      isClosing={isClosing}
      animate
      presentation="sheet"
      forceMobileSheet={isMobileSheetLayout}
      unwrapped={!isMobileSheetLayout}
      showSheetHandle={isMobileSheetLayout}
      keyboardAware={!isMobileSheetLayout}
      panelClassName={isMobileSheetLayout ? mobileSheetPanelClass : undefined}
      className={`login-modal-overlay${variant === 'figma-auth' ? ' login-modal-overlay--figma-auth' : ''}`}
      aria-label={getTitle()}
    >
      <div
        className={
          isMobileSheetLayout
            ? useFigmaMobileSheet
              ? 'login-modal-figma-sheet-shell'
              : 'login-modal-mobile-sheet-shell'
            : 'mx-auto w-full max-w-[600px] px-4 max-md:px-4'
        }
        onClick={(e) => e.stopPropagation()}
      >
      <div
        className={`login-modal ${isClosing ? 'closing' : ''} ${isMethodStep ? 'login-modal--method-step' : ''} ${useFigmaMethodStep && isMethodStep ? 'login-modal--figma-method' : ''} ${isFormStep ? 'login-modal--form-step' : ''}`}
      >
        <button className="login-modal-close" onClick={handleClose} aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Hero Image Section — hidden on Figma method step and mobile form steps */}
        {showHero ? (
          <div className="login-modal-hero">
            <img
              src={travelerBackground}
              alt="Travel background"
              className="login-modal-hero-image"
            />
            <div className="login-modal-gradient"></div>
            <div className="login-modal-hero-content">
              <div className="login-modal-logo-overlay">
                <img src={logoOrangeIcon} alt="Globe Thrivers" className="login-modal-logo" />
              </div>
              <h2 className="login-modal-title">
                {getTitle()}
              </h2>
              <p className="login-modal-description">
                {getDescription()}
              </p>
            </div>
          </div>
        ) : null}

        {/* Content Section */}
        <div className="login-modal-content gt-keyboard-scroll" ref={contentRef}>
          {/* Step 1: Method picker — Google, Apple, email CTA */}
          {isMethodStep && (
            <>
              {useFigmaMethodStep && (
                <div className="login-modal-figma-header">
                  <img src={logoOrangeIcon} alt="Globe Thrivers" className="login-modal-figma-logo" />
                  <h2 className="login-modal-figma-title">
                    {mode === 'login' ? 'Welcome back' : 'Create an Account'}
                  </h2>
                  <p className="login-modal-figma-description">{getDescription()}</p>
                </div>
              )}

              <div className="login-modal-oauth-buttons">
                {isInAppBrowser ? (
                  <p className="login-modal-inapp-notice">
                    For Google or Apple sign-in, open this page in Safari or Chrome.
                  </p>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => handleOAuthClick('Google')}
                      className="login-modal-oauth-button"
                    >
                      <img src={google} alt="" className="login-modal-oauth-icon" />
                      <span>{useFigmaMethodStep ? 'Continue With Google' : (mode === 'signup' ? 'Sign up with Google' : 'Log in with Google')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleOAuthClick('Apple')}
                      className="login-modal-oauth-button"
                    >
                      <img src={apple} alt="" className="login-modal-oauth-icon" />
                      <span>{useFigmaMethodStep ? 'Continue With Apple' : (mode === 'signup' ? 'Sign up with Apple' : 'Log in with Apple')}</span>
                    </button>
                  </>
                )}

                {!useFigmaMethodStep && (
                  <div className="login-modal-divider">
                    <div className="login-modal-divider-line"></div>
                    <span className="login-modal-divider-text">Or</span>
                    <div className="login-modal-divider-line"></div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => {
                    WebAnalytics.track(EVENT_AUTH_METHOD_SELECTED, {
                      method: 'email',
                      mode,
                      variant,
                    });
                    setShowEmailAuth(true);
                  }}
                  className="login-modal-oauth-button"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="login-modal-oauth-icon"
                    aria-hidden
                    focusable="false"
                  >
                    <path d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="#525257"/>
                  </svg>
                  <span>
                    {useFigmaMethodStep
                      ? 'Continue With Email'
                      : mode === 'login'
                        ? 'Log in with email'
                        : 'Sign up with email'}
                  </span>
                </button>
              </div>

              <div className={`login-modal-toggle ${useFigmaMethodStep ? 'login-modal-toggle--figma' : ''}`}>
                <span>
                  {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const nextMode = mode === 'login' ? 'signup' : 'login';
                    WebAnalytics.track(EVENT_AUTH_MODE_TOGGLED, {
                      from_mode: mode,
                      to_mode: nextMode,
                      variant,
                    });
                    setMode(nextMode);
                    setErrors({});
                  }}
                  className="login-modal-toggle-link"
                >
                  {mode === 'login' ? 'Sign up' : 'Log In'}
                </button>
              </div>
            </>
          )}

          {/* Step 2: Email / password form */}
          {(mode === 'login' || mode === 'signup') && showEmailAuth && (
            <>
              {prefilledAccount ? (
                // Mobile form steps hide the hero, which otherwise carries the title.
                !showHero && (
                  <div className="login-modal-figma-header">
                    <h2 className="login-modal-figma-title">{getTitle()}</h2>
                    <p className="login-modal-figma-description">{getDescription()}</p>
                  </div>
                )
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setShowEmailAuth(false);
                    setErrors({});
                  }}
                  className="login-modal-back-link"
                >
                  ← Other sign-in options
                </button>
              )}

              <form onSubmit={handleSubmit} className="login-modal-form">
                <InputField
                  label="Email"
                  type="email"
                  value={form.email}
                  readOnly={Boolean(prefilledAccount)}
                  error={errors.email}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm({ ...form, email: value });

                    if (!value) {
                      setErrors((prev) => ({ ...prev, email: 'Email is required' }));
                    } else if (!validateEmail(value)) {
                      setErrors((prev) => ({ ...prev, email: 'Invalid email format' }));
                    } else {
                      setErrors((prev) => {
                        const { email: _email, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  onBlur={(e) => {
                    if (!e.target.value) {
                      setErrors((prev) => ({ ...prev, email: 'Email is required' }));
                    } else if (!validateEmail(e.target.value)) {
                      setErrors((prev) => ({ ...prev, email: 'Invalid email format' }));
                    }
                  }}
                />
                <InputField
                  type="password"
                  label="Password"
                  value={form.password}
                  error={errors.password}
                  onChange={(e) => {
                    const value = e.target.value;
                    setForm({ ...form, password: value });

                    if (!value) {
                      setErrors((prev) => ({
                        ...prev,
                        password: 'Password is required'
                      }));
                    } else {
                      const passwordError = getPasswordValidationError(value);
                      if (passwordError) {
                        setErrors((prev) => ({ ...prev, password: passwordError }));
                      } else {
                        setErrors((prev) => {
                          const { password: _password, ...rest } = prev;
                          return rest;
                        });
                      }
                    }
                  }}
                  onBlur={(e) => {
                    if (!e.target.value) {
                      setErrors((prev) => ({
                        ...prev,
                        password: 'Password is required'
                      }));
                    } else {
                      const passwordError = getPasswordValidationError(e.target.value);
                      if (passwordError) {
                        setErrors((prev) => ({ ...prev, password: passwordError }));
                      }
                    }
                  }}
                />

                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={() => {
                      WebAnalytics.track(EVENT_FORGOT_PASSWORD_CLICKED, { mode, variant });
                      setMode('forgotPassword');
                      setResetEmail(form.email);
                      setErrors({});
                    }}
                    className="login-modal-forgot-link"
                  >
                    Forgot password?
                  </button>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="login-modal-submit-button"
                >
                  {loading
                    ? 'Please wait...'
                    : mode === 'login'
                      ? 'Log In'
                      : prefilledAccount
                        ? 'Create Account'
                        : 'Sign Up'}
                </button>
              </form>

              <div className="login-modal-toggle">
                <span>
                  {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    const nextMode = mode === 'login' ? 'signup' : 'login';
                    WebAnalytics.track(EVENT_AUTH_MODE_TOGGLED, {
                      from_mode: mode,
                      to_mode: nextMode,
                      variant,
                    });
                    setMode(nextMode);
                    setErrors({});
                  }}
                  className="login-modal-toggle-link"
                >
                  {mode === 'login' ? 'Sign up' : 'Log in'}
                </button>
              </div>
            </>
          )}

          {mode === 'forgotPassword' && (
            <>
              {!codeSent ? (
                <form onSubmit={handleForgotPassword} className="login-modal-form">
                  {errors.general && (
                    <div 
                      className="login-modal-error-message"
                      role="alert"
                      style={{
                        backgroundColor: '#FEF2F2',
                        border: '1px solid #FECACA',
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '16px',
                        color: '#991B1B',
                        fontSize: '14px'
                      }}
                    >
                      {errors.general}
                      {showResendVerification && (
                        <button
                          type="button"
                          onClick={handleResendVerificationEmail}
                          style={{
                            display: 'block',
                            marginTop: '8px',
                            background: 'none',
                            border: 'none',
                            color: '#FF5212',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontSize: '14px',
                            fontFamily: 'Manrope, sans-serif'
                          }}
                        >
                          Resend Verification Email
                        </button>
                      )}
                      {errors.general?.includes('Google/Apple') && (
                        <div style={{ marginTop: '12px' }}>
                          <button
                            type="button"
                            onClick={() => handleOAuthClick('Google')}
                            className="login-modal-oauth-button"
                            style={{ marginBottom: '8px' }}
                          >
                            <img src={google} alt="" className="login-modal-oauth-icon" />
                            <span>Log in with Google</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOAuthClick('Apple')}
                            className="login-modal-oauth-button"
                          >
                            <img src={apple} alt="" className="login-modal-oauth-icon" />
                            <span>Log in with Apple</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  <InputField
                    label="Email"
                    type="email"
                    value={resetEmail}
                    error={errors.email}
                    onChange={(e) => {
                      const value = e.target.value;
                      setResetEmail(value);
                      if (!value) {
                        setErrors((prev) => ({ ...prev, email: 'Email is required' }));
                      } else if (!validateEmail(value)) {
                        setErrors((prev) => ({ ...prev, email: 'Invalid email format' }));
                      } else {
                        setErrors((prev) => {
                          const { email: _email, ...rest } = prev;
                          return rest;
                        });
                      }
                    }}
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="login-modal-submit-button"
                  >
                    {loading ? 'Sending...' : 'Send Reset Code'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMode('login');
                      setShowEmailAuth(true);
                      setResetEmail('');
                      setErrors({});
                    }}
                    className="login-modal-toggle-link"
                    style={{ marginTop: '8px' }}
                  >
                    ← Back to login
                  </button>
                </form>
              ) : (
                <div className="login-modal-form">
                  <div 
                    style={{
                      backgroundColor: '#F0FDF4',
                      border: '1px solid #BBF7D0',
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '16px',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: '48px', marginBottom: '8px' }}>✓</div>
                    <p style={{ color: '#166534', fontSize: '14px', margin: '0 0 8px 0', fontWeight: '500' }}>
                      Verification code sent to {maskEmail(resetEmail)}
                    </p>
                    <p style={{ color: '#166534', fontSize: '13px', margin: 0, opacity: 0.9 }}>
                      Please check your email inbox (and spam folder) for a 6-digit code.
                    </p>
                  </div>
                  
                  <div 
                    style={{
                      backgroundColor: '#F9FAFB',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '16px',
                      fontSize: '13px',
                      color: '#6B7280',
                      lineHeight: '1.5'
                    }}
                  >
                    <p style={{ margin: '0 0 8px 0', fontWeight: '500', color: '#374151' }}>
                      What to do next:
                    </p>
                    <ol style={{ margin: 0, paddingLeft: '20px', textAlign: 'left' }}>
                      <li style={{ marginBottom: '4px' }}>Check your email inbox for the verification code</li>
                      <li style={{ marginBottom: '4px' }}>Find the email from Globe Thrivers with your 6-digit code</li>
                      <li style={{ marginBottom: '4px' }}>Copy or note down the 6-digit verification code</li>
                      <li>Click &quot;Continue&quot; below to enter the code and reset your password</li>
                    </ol>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setMode('resetPassword');
                      setErrors({});
                    }}
                    className="login-modal-submit-button"
                  >
                    Continue
                  </button>
                  <div style={{ textAlign: 'center', marginTop: '12px' }}>
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={!canResendCode || loading}
                      className="login-modal-toggle-link"
                      style={{
                        opacity: canResendCode ? 1 : 0.5,
                        cursor: canResendCode ? 'pointer' : 'not-allowed'
                      }}
                    >
                      {canResendCode 
                        ? 'Resend Code' 
                        : `Resend Code (in ${resendTimer}s)`}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setMode('login');
                      setShowEmailAuth(true);
                      setResetEmail('');
                      setCodeSent(false);
                      setErrors({});
                    }}
                    className="login-modal-toggle-link"
                    style={{ marginTop: '8px' }}
                  >
                    ← Back to login
                  </button>
                </div>
              )}
            </>
          )}

          {mode === 'resetPassword' && (
            <form onSubmit={handleResetPassword} className="login-modal-form">
              <div 
                style={{
                  backgroundColor: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  color: '#6B7280',
                  lineHeight: '1.5'
                }}
              >
                <p style={{ margin: '0 0 8px 0', fontWeight: '500', color: '#374151' }}>
                  Enter your verification code:
                </p>
                <p style={{ margin: 0 }}>
                  Enter the 6-digit code from your email. You can type it directly or paste it into the field below.
                </p>
              </div>

              <InputField
                label="Verification Code"
                type="text"
                value={verificationCode}
                error={errors.verificationCode}
                placeholder="123456"
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerificationCode(value);
                  if (!value) {
                    setErrors((prev) => ({ ...prev, verificationCode: 'Verification code is required' }));
                  } else if (!validateVerificationCode(value)) {
                    setErrors((prev) => ({ ...prev, verificationCode: 'Please enter the 6-digit verification code' }));
                  } else {
                    setErrors((prev) => {
                      const { verificationCode: _verificationCode, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
              />
              
              <div 
                style={{
                  backgroundColor: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px',
                  fontSize: '13px',
                  color: '#6B7280',
                  lineHeight: '1.5'
                }}
              >
                <p style={{ margin: '0 0 8px 0', fontWeight: '500', color: '#374151' }}>
                  Create your new password:
                </p>
                <p style={{ margin: 0 }}>
                  Your password must be at least 8 characters and include uppercase and lowercase letters, a number, and a special character (any symbol; no spaces).
                </p>
              </div>
              <InputField
                type="password"
                label="New Password"
                value={newPassword}
                error={errors.newPassword}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewPassword(value);
                  if (!value) {
                    setErrors((prev) => ({
                      ...prev,
                      newPassword: 'New password is required'
                    }));
                  } else {
                    const passwordError = getPasswordValidationError(value);
                    if (passwordError) {
                      setErrors((prev) => ({ ...prev, newPassword: passwordError }));
                    } else {
                      setErrors((prev) => {
                        const { newPassword: _newPassword, ...rest } = prev;
                        return rest;
                      });
                    }
                  }
                }}
              />
              <InputField
                type="password"
                label="Confirm Password"
                value={confirmPasswordValue}
                error={errors.confirmPassword}
                onChange={(e) => {
                  const value = e.target.value;
                  setConfirmPasswordValue(value);
                  if (!value) {
                    setErrors((prev) => ({
                      ...prev,
                      confirmPassword: 'Please confirm your password'
                    }));
                  } else if (newPassword && value !== newPassword) {
                    setErrors((prev) => ({
                      ...prev,
                      confirmPassword: 'Passwords do not match'
                    }));
                  } else {
                    setErrors((prev) => {
                      const { confirmPassword: _confirmPassword, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
              />
              {errors.general && (
                <div 
                  role="alert"
                  style={{
                    backgroundColor: '#FEF2F2',
                    border: '1px solid #FECACA',
                    borderRadius: '8px',
                    padding: '12px',
                    color: '#991B1B',
                    fontSize: '14px'
                  }}
                >
                  {errors.general}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="login-modal-submit-button"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
              <div style={{ textAlign: 'center', marginTop: '12px' }}>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={!canResendCode || loading}
                  className="login-modal-toggle-link"
                  style={{
                    opacity: canResendCode ? 1 : 0.5,
                    cursor: canResendCode ? 'pointer' : 'not-allowed'
                  }}
                >
                  {canResendCode 
                    ? 'Resend Code' 
                    : `Resend Code (in ${resendTimer}s)`}
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  setShowEmailAuth(true);
                  setResetEmail('');
                  setVerificationCode('');
                  setNewPassword('');
                  setConfirmPasswordValue('');
                  setCodeSent(false);
                  setErrors({});
                }}
                className="login-modal-toggle-link"
                style={{ marginTop: '8px' }}
              >
                ← Back to login
              </button>
            </form>
          )}
        </div>
      </div>
      </div>
    </ResponsiveOverlay>
  );

  // Handle banned user dialog close - logout and redirect to home
  const handleBannedDialogClose = async () => {
    setShowBannedDialog(false);
    setBannedMessage('');
    setIsDeletedAccount(false);
    onClose();
    // Use window.location.href for a full page reload to ensure clean state
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  // BannedUserDialog portals via ModalOverlay; main login modal portals via ModalOverlay too.
  if (showBannedDialog) {
    return (
      <BannedUserDialog
        message={bannedMessage}
        onClose={handleBannedDialogClose}
        isDeleted={isDeletedAccount}
      />
    );
  }

  if (isAuthTransitioning && typeof document !== 'undefined') {
    return createPortal(
      <div className="login-auth-transition" data-testid="login-auth-transition">
        <OnboardingSplashBackground />
        <div className="login-auth-transition__spinner" aria-live="polite" aria-busy="true">
          <div className="login-auth-transition__spinner-ring" />
        </div>
      </div>,
      document.body,
    );
  }

  return modalContent;
};

export default LoginModal;
